import { useQuery } from "@tanstack/react-query";
import axios from "axios";

/**
 * Plan data from the public plans API
 */
export interface PublicPlan {
  slug: string;
  display_name: string;
  price_cents: number;
  original_price_cents: number | null;
  currency: string;
  interval: string;
  payment_link: string | null;
  features: string[];
  stripe_price_id: string;
}

/**
 * Fetches public plan data from the API.
 * No authentication required - this is public pricing data.
 *
 * Usage:
 *   const { plans, isLoading, isError } = usePublicPlans();
 *   const launchAssist = plans?.find(p => p.slug === "launch_assist");
 */
export const usePublicPlans = (currency: string = "USD") => {
  const cur = currency.toLowerCase();

  const fetchPlans = async (): Promise<PublicPlan[]> => {
    // Use relative URL for same-origin requests, absolute for cross-origin (landing page)
    const baseUrl =
      typeof window !== "undefined" &&
      window.location.hostname === "moonscape.app"
        ? "https://dashboard.moonscape.app/api"
        : "/api";

    const response = await axios.get(`${baseUrl}/public/plans?currency=${cur}`);
    return response.data;
  };

  const {
    data: plans,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["public-plans", cur],
    queryFn: fetchPlans,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  /**
   * Get a specific plan by slug
   */
  const getPlan = (slug: string): PublicPlan | undefined => {
    return plans?.find((p) => p.slug === slug);
  };

  /**
   * Format price for display
   */
  const formatPrice = (plan: PublicPlan): string => {
    const symbol = plan.currency === "USD" ? "$" : plan.currency === "GBP" ? "£" : "€";
    return `${symbol}${Math.round(plan.price_cents / 100)}`;
  };

  /**
   * Calculate discount percentage
   */
  const getDiscountPercent = (plan: PublicPlan): number => {
    if (!plan.original_price_cents || plan.original_price_cents <= plan.price_cents) {
      return 0;
    }
    return Math.round(
      ((plan.original_price_cents - plan.price_cents) / plan.original_price_cents) * 100
    );
  };

  return {
    plans,
    isLoading,
    isError,
    error,
    getPlan,
    formatPrice,
    getDiscountPercent,
  };
};

export default usePublicPlans;
